import { OrganizationSettingsDTOMaximumPolicyCoverEnum, OrganizationSettingsDTOPricingRuleEnum } from '@reposit/api-client';
import { Formik } from 'formik';
import React from 'react';
import { Container, Row } from 'react-grid-system';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import * as Yup from 'yup';
import Button from '../../components/Button';
import FlashMessage from '../../components/FlashMessage';
import FieldWithLabel from '../../components/FormFields/FieldWithLabel';
import Select from '../../components/FormFields/Select';
import Modal from '../../components/Modal';
import { Header3 } from '../../components/Typography';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../FlashMessage';
import { createLoadingSelector } from '../../redux/loading/loading.selector';
import {
  UPDATE_ORGANIZATION_POLICY_COVER_CAP_MODAL_STORE_KEY,
  UPDATE_ORGANIZATION_POLICY_COVER_CAP_STORE_KEY,
  updateOrganizationPolicyCoverCapRequested,
} from '../../redux/organization/organization.actions';
import { isCappedPricingRule } from './policy-cover-cap-rules';

export type PolicyCoverCapLabels = {
  [key in OrganizationSettingsDTOMaximumPolicyCoverEnum]: string;
};

interface EditPolicyCoverCapModalProps {
  organizationId: string;
  policyCoverCap?: OrganizationSettingsDTOMaximumPolicyCoverEnum;
  policyCoverCapLabels: PolicyCoverCapLabels;
  pricingRule?: OrganizationSettingsDTOPricingRuleEnum;
  onDismiss: () => void;
}

interface EditPolicyCoverCapFormValues {
  maximumPolicyCover: OrganizationSettingsDTOMaximumPolicyCoverEnum;
}

const policyCoverCapOptions = [
  OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS,
  OrganizationSettingsDTOMaximumPolicyCoverEnum.TENTHOUSANDPOUNDS,
];

const Actions = styled(Row)`
  display: flex;
  justify-content: flex-end;
  column-gap: 8px;
  padding-top: 24px;
`;

const EditPolicyCoverCapModal: React.FC<EditPolicyCoverCapModalProps> = ({
  organizationId,
  policyCoverCap = OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS,
  policyCoverCapLabels,
  pricingRule,
  onDismiss,
}) => {
  const dispatch = useDispatch();
  const isSubmitting = useSelector(createLoadingSelector([UPDATE_ORGANIZATION_POLICY_COVER_CAP_STORE_KEY]));
  const [flashMessage, onDismissFlashMessage] = useFlashMessage([UPDATE_ORGANIZATION_POLICY_COVER_CAP_MODAL_STORE_KEY]);
  const allowedPolicyCoverCapOptions = policyCoverCapOptions.filter(
    (option) =>
      !isCappedPricingRule(pricingRule) || option === OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS,
  );
  const validationSchema = Yup.object().shape({
    maximumPolicyCover: Yup.string()
      .oneOf(allowedPolicyCoverCapOptions, 'Capped fees require a £5,000 policy cover cap')
      .required('Required'),
  });
  const initialPolicyCoverCap = allowedPolicyCoverCapOptions.includes(policyCoverCap)
    ? policyCoverCap
    : OrganizationSettingsDTOMaximumPolicyCoverEnum.FIVETHOUSANDPOUNDS;

  return (
    <Modal onDismiss={onDismiss}>
      {flashMessage ? (
        <FlashMessage payload={flashMessage} onDismiss={onDismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}
      <Header3 style={{ marginBottom: 15 }}>Edit Policy Cover Cap</Header3>
      <Formik<EditPolicyCoverCapFormValues>
        initialValues={{
          maximumPolicyCover: initialPolicyCoverCap,
        }}
        validationSchema={validationSchema}
        onSubmit={(values) => {
          dispatch(updateOrganizationPolicyCoverCapRequested({ organizationId, maximumPolicyCover: values.maximumPolicyCover }));
        }}
      >
        {({ errors, handleBlur, handleSubmit, setFieldValue, touched, values }) => (
          <form onSubmit={handleSubmit}>
            <FieldWithLabel
              label="Policy Cover Cap"
              error={errors.maximumPolicyCover}
              touched={touched.maximumPolicyCover}
            >
              <Select
                name="maximumPolicyCover"
                onChange={(value) => setFieldValue('maximumPolicyCover', value)}
                onBlur={handleBlur}
                value={values.maximumPolicyCover}
              >
                {allowedPolicyCoverCapOptions.map((option) => (
                  <option key={option} value={option}>
                    {policyCoverCapLabels[option]}
                  </option>
                ))}
              </Select>
            </FieldWithLabel>
            <Container fluid>
              <Actions>
                <Button buttonType="secondary" noArrow={true} onClick={onDismiss} type="button">
                  Cancel
                </Button>
                <Button disabled={isSubmitting} type="submit">
                  Save
                </Button>
              </Actions>
            </Container>
          </form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditPolicyCoverCapModal;
